import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Collapsible, CollapsibleContent } from "@/components/ui/collapsible";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Edit, Plus, ChevronDown, ChevronRight, Search, Trash2, Loader2 } from "lucide-react";
import { DepartmentProcesses } from "./DepartmentProcesses";
import {
  useDepartments,
  useCreateDepartment,
  useUpdateDepartment,
  useDeleteDepartment,
} from "@/hooks/useDepartments";
import type { Department, DepartmentCreate, DepartmentUpdate } from "@/services/departmentApi";

// Generate consistent color for department based on code
const getDepartmentColor = (code: string) => {
  const colors = [
    "bg-red-500",
    "bg-blue-500", 
    "bg-green-500",
    "bg-yellow-500",
    "bg-purple-500",
    "bg-pink-500",
    "bg-indigo-500",
    "bg-cyan-500",
    "bg-orange-500",
    "bg-teal-500"
  ];
  
  const hash = code.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return colors[hash % colors.length];
};

export function DepartmentsConfiguration() {
  const { data: departments = [], isLoading } = useDepartments();
  const createDepartmentMutation = useCreateDepartment();
  const updateDepartmentMutation = useUpdateDepartment();
  const deleteDepartmentMutation = useDeleteDepartment();
  
  const [expandedDepts, setExpandedDepts] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [newDeptCode, setNewDeptCode] = useState("");
  const [newDeptName, setNewDeptName] = useState("");
  const [newDeptDescription, setNewDeptDescription] = useState("");
  
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingDept, setEditingDept] = useState<Department | null>(null);
  const [editDeptCode, setEditDeptCode] = useState("");
  const [editDeptName, setEditDeptName] = useState("");
  const [editDeptDescription, setEditDeptDescription] = useState("");
  
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deletingDeptId, setDeletingDeptId] = useState<string | null>(null);

  const toggleExpanded = (deptId: string) => {
    setExpandedDepts((prev) =>
      prev.includes(deptId)
        ? prev.filter((id) => id !== deptId)
        : [...prev, deptId]
    );
  };

  const filteredDepartments = departments.filter(
    (dept) =>
      dept.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      dept.code.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (dept.description || "").toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleCreateDepartment = async () => {
    if (!newDeptCode.trim() || !newDeptName.trim()) return;

    const newDepartment: DepartmentCreate = {
      code: newDeptCode.trim().toUpperCase(),
      name: newDeptName.trim(),
      description: newDeptDescription.trim() || null,
      capacity: 1,
    };

    createDepartmentMutation.mutate(newDepartment, {
      onSuccess: (createdDept) => {
        // Reset form
        setNewDeptCode("");
        setNewDeptName("");
        setNewDeptDescription("");
        setIsAddDialogOpen(false);
        
        // Auto-expand the new department
        setExpandedDepts([...expandedDepts, createdDept.id]);
      },
    });
  };

  const handleEditClick = (dept: Department, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingDept(dept);
    setEditDeptCode(dept.code);
    setEditDeptName(dept.name);
    setEditDeptDescription(dept.description || "");
    setIsEditDialogOpen(true);
  };

  const handleUpdateDepartment = async () => {
    if (!editDeptCode.trim() || !editDeptName.trim() || !editingDept) return;

    const updateData: DepartmentUpdate = {
      code: editDeptCode.trim().toUpperCase(),
      name: editDeptName.trim(),
      description: editDeptDescription.trim() || null,
    };

    updateDepartmentMutation.mutate(
      { id: editingDept.id, data: updateData },
      {
        onSuccess: () => {
          setIsEditDialogOpen(false);
          setEditingDept(null);
        },
      }
    );
  };

  const handleDeleteClick = (deptId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setDeletingDeptId(deptId);
    setIsDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deletingDeptId) return;

    deleteDepartmentMutation.mutate(deletingDeptId, {
      onSuccess: () => {
        setIsDeleteDialogOpen(false);
        setDeletingDeptId(null);
      },
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center gap-4">
        <h3 className="text-lg font-medium">部門列表</h3>
        <div className="flex items-center gap-3 flex-1 max-w-md">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="搜尋部門..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
          <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
            <DialogTrigger asChild>
              <Button className="flex items-center gap-2">
                <Plus className="h-4 w-4" />
                新增部門
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>新增部門</DialogTitle>
                <DialogDescription>
                  建立新的部門並設定基本資訊
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label htmlFor="deptCode">部門代碼</Label>
                  <Input
                    id="deptCode"
                    value={newDeptCode}
                    onChange={(e) => setNewDeptCode(e.target.value)}
                    placeholder="如: CUT, SEW"
                    className="uppercase"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="deptName">部門名稱</Label>
                  <Input
                    id="deptName"
                    value={newDeptName}
                    onChange={(e) => setNewDeptName(e.target.value)}
                    placeholder="請輸入部門名稱"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="deptDescription">部門描述</Label>
                  <Textarea
                    id="deptDescription"
                    value={newDeptDescription}
                    onChange={(e) => setNewDeptDescription(e.target.value)}
                    placeholder="請描述此部門的職責"
                    rows={3}
                  />
                </div>

                <div className="flex justify-end gap-2 pt-4">
                  <Button
                    variant="outline"
                    onClick={() => setIsAddDialogOpen(false)}
                  >
                    取消
                  </Button>
                  <Button
                    onClick={handleCreateDepartment}
                    disabled={!newDeptCode.trim() || !newDeptName.trim() || createDepartmentMutation.isPending}
                  >
                    {createDepartmentMutation.isPending ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        創建中...
                      </>
                    ) : (
                      "創建部門"
                    )}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredDepartments.map((dept) => {
          const isExpanded = expandedDepts.includes(dept.id);
          return (
            <Card
              key={dept.id}
              className={`transition-all hover:shadow-md ${
                isExpanded ? "md:col-span-2 lg:col-span-3" : ""
              }`}
            >
              {!isExpanded ? (
                <div
                  className="p-4 cursor-pointer flex items-center justify-between group"
                  onClick={() => toggleExpanded(dept.id)}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div
                      className={`w-3 h-3 rounded-full flex-shrink-0 ${getDepartmentColor(dept.code)}`}
                    />
                    <div className="flex items-center gap-1.5">
                      <h4 className="font-medium text-sm truncate">
                        {dept.name}
                      </h4>
                      <span className="text-xs font-mono bg-muted text-muted-foreground px-2 py-0.5 rounded-full">
                        {dept.code}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={(e) => handleEditClick(dept, e)}
                    >
                      <Edit className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 hover:bg-red-50 dark:hover:bg-red-950"
                      onClick={(e) => handleDeleteClick(dept.id, e)}
                    >
                      <Trash2 className="h-3.5 w-3.5 text-red-600" />
                    </Button>
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  </div>
                </div>
              ) : (
                <CardHeader
                  className="pb-3 cursor-pointer"
                  onClick={() => toggleExpanded(dept.id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3 pl-2">
                      <div
                        className={`w-4 h-4 rounded-full flex-shrink-0 ${getDepartmentColor(dept.code)}`}
                      />
                      <div className="flex items-center gap-2">
                        <h4 className="font-medium text-base">{dept.name}</h4>
                        <span className="text-sm font-mono bg-muted text-muted-foreground px-2.5 py-1 rounded-full">
                          {dept.code}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={(e) => handleEditClick(dept, e)}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 hover:bg-red-50 dark:hover:bg-red-950"
                        onClick={(e) => handleDeleteClick(dept.id, e)}
                      >
                        <Trash2 className="h-4 w-4 text-red-600" />
                      </Button>
                      <ChevronDown className="h-5 w-5 text-muted-foreground" />
                    </div>
                  </div>
                </CardHeader>
              )}

              <Collapsible open={isExpanded}>
                <CollapsibleContent>
                  <CardContent className="pt-0">
                    <div className="space-y-4">
                      {dept.description && (
                        <div className="space-y-2">
                          <p className="text-sm font-medium text-muted-foreground">
                            描述
                          </p>
                          <Textarea
                            value={dept.description}
                            readOnly
                            className="resize-none min-h-[60px] text-sm bg-muted/50"
                            rows={2}
                          />
                        </div>
                      )}

                      <div className="border-t pt-4">
                        <DepartmentProcesses
                          departmentId={dept.id}
                          departmentName={dept.name}
                        />
                      </div>
                    </div>
                  </CardContent>
                </CollapsibleContent>
              </Collapsible>
            </Card>
          );
        })}
      </div>

      {filteredDepartments.length === 0 && (
        <div className="text-center py-8 text-muted-foreground">
          <p>找不到符合的部門</p>
        </div>
      )}

      {/* Edit Department Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>編輯部門</DialogTitle>
            <DialogDescription>
              修改部門的基本資訊
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="editDeptCode">部門代碼</Label>
              <Input
                id="editDeptCode"
                value={editDeptCode}
                onChange={(e) => setEditDeptCode(e.target.value)}
                placeholder="如: CUT, SEW"
                className="uppercase"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="editDeptName">部門名稱</Label>
              <Input
                id="editDeptName"
                value={editDeptName}
                onChange={(e) => setEditDeptName(e.target.value)}
                placeholder="請輸入部門名稱"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="editDeptDescription">部門描述</Label>
              <Textarea
                id="editDeptDescription"
                value={editDeptDescription}
                onChange={(e) => setEditDeptDescription(e.target.value)}
                placeholder="請描述此部門的職責"
                rows={3}
              />
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button
                variant="outline"
                onClick={() => setIsEditDialogOpen(false)}
              >
                取消
              </Button>
              <Button
                onClick={handleUpdateDepartment}
                disabled={!editDeptCode.trim() || !editDeptName.trim() || updateDepartmentMutation.isPending}
              >
                {updateDepartmentMutation.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    更新中...
                  </>
                ) : (
                  "更新"
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>確認刪除</DialogTitle>
            <DialogDescription>
              您確定要刪除這個部門嗎？此操作無法復原。
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2 pt-4">
            <Button
              variant="outline"
              onClick={() => setIsDeleteDialogOpen(false)}
            >
              取消
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmDelete}
              disabled={deleteDepartmentMutation.isPending}
            >
              {deleteDepartmentMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  刪除中...
                </>
              ) : (
                "確認刪除"
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}